/**
 * Trend Rover V2 Strategy — faithful port of MT4 Trend_Rover_V2.mq4
 *
 * CPR/Pivot-based strategy (NOT SuperTrend). Uses first candle of the day
 * on the selected timeframe to compute buy AND sell levels simultaneously.
 *
 * Formulas (select via param):
 *   Sentinel — Fibonacci-based pivot levels with StrategyValue offset
 *   Crafter  — Golden-ratio pivot levels
 *   Resistor — Square-root mathematical projection
 *
 * Timeframe sub-param:
 *   Strategy-1 → M15 (StrategyValue = 0)
 *   Strategy-2 → M30 (StrategyValue = 0.03)
 *
 * All levels rounded to nearest 0.05: Math.round(val / 0.05) * 0.05
 *
 * Shows BOTH buy and sell levels simultaneously (8 horizontal lines).
 * NO candle coloring — no candleColorOwnerRef interaction.
 * NO trailing stop.
 * Daily reset: all values recalculate on each new trading day.
 *
 * MT4 features ported:
 *   - First candle of day detection via timestamp boundary
 *   - 3 formula strategies with 2 timeframe options each
 *   - 8 simultaneous level lines (buy + sell)
 *   - Buy/Sell activation detection
 *   - Target/SL hit tracking
 *   - Dashboard metadata for overlay component
 *
 * Category: Strategies | Pane: Overlay
 */
import { IndicatorDefinition, PlotData, IndicatorPriceLine, IndicatorMarker } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";
import { inferTickSize, roundToTick } from "./price-utils";

// IST offset (UTC+5:30) in seconds for day boundary detection
const IST_OFFSET_SEC = 5 * 3600 + 30 * 60;


// ─── Dashboard interface ────────────────────────────────────────
export interface TrendRoverDashboard {
  formula: string;
  timeframeSub: string;
  pivotPrice: number | null;
  dayRange: number | null;
  buyEntry: number | null;
  buyTP1: number | null;
  buyTP2: number | null;
  buySL: number | null;
  sellEntry: number | null;
  sellTP1: number | null;
  sellTP2: number | null;
  sellSL: number | null;
  buyActive: boolean;
  sellActive: boolean;
  buyTP1Hit: boolean;
  buyTP2Hit: boolean;
  buySLHit: boolean;
  sellTP1Hit: boolean;
  sellTP2Hit: boolean;
  sellSLHit: boolean;
  currentPrice: number;
  buyLivePL: number;
  sellLivePL: number;
  /** Current trade's TP1 partial exit already locked in (resets on new entry) */
  buyBookedPL: number;
  sellBookedPL: number;
  /** Cumulative P&L from ALL completed trades today (not reset on re-entry) */
  buyDayPL: number;
  sellDayPL: number;
  /** How many times price activated each entry today */
  buyTradeCount: number;
  sellTradeCount: number;
  buySLCount: number;
  sellSLCount: number;
  buyTP1Count: number;
  buyTP2Count: number;
  sellTP1Count: number;
  sellTP2Count: number;
}

// ─── Level computation per formula ──────────────────────────────
interface Levels {
  buyEntry: number; buyTP1: number; buyTP2: number; buySL: number;
  sellEntry: number; sellTP1: number; sellTP2: number; sellSL: number;
}

function computeSentinel(pp: number, dr: number, sv: number, tick: number): Levels {
  return {
    buyEntry:  roundToTick(pp + sv + 0.618 * dr, tick),
    buyTP1:    roundToTick(pp + sv + 1.175 * dr, tick),
    buyTP2:    roundToTick(pp + sv + 1.732 * dr, tick),
    buySL:     roundToTick(pp + sv + 0.061 * dr, tick),
    sellEntry: roundToTick(pp + sv - 0.618 * dr, tick),
    sellTP1:   roundToTick(pp + sv - 1.175 * dr, tick),
    sellTP2:   roundToTick(pp + sv - 1.732 * dr, tick),
    sellSL:    roundToTick(pp + sv - 0.061 * dr, tick),
  };
}

function computeCrafter(pp: number, dr: number, tick: number): Levels {
  const buyTP1  = roundToTick(pp + 0.896 * dr, tick);
  const buyTP2  = roundToTick(pp + 1.396 * dr, tick);
  const buyEntry = roundToTick((pp + buyTP1) / 2.0, tick);
  const sellTP1  = roundToTick(pp - 0.896 * dr, tick);
  const sellTP2  = roundToTick(pp - 1.396 * dr, tick);
  const sellEntry = roundToTick((pp + sellTP1) / 2.0, tick);
  const buySL    = roundToTick((pp + sellEntry) / 2.0, tick);
  const sellSL   = roundToTick((pp + buyEntry) / 2.0, tick);
  return { buyEntry, buyTP1, buyTP2, buySL, sellEntry, sellTP1, sellTP2, sellSL };
}

function computeResistor(pp: number, tick: number): Levels {
  const sqrtPP = Math.sqrt(pp);
  return {
    buyEntry:  roundToTick(Math.pow(sqrtPP + 0.175, 2), tick),
    buyTP1:    roundToTick(Math.pow(sqrtPP + 0.405, 2), tick),
    buyTP2:    roundToTick(Math.pow(sqrtPP + 0.633, 2), tick),
    buySL:     roundToTick(Math.pow(sqrtPP - 0.115, 2), tick),
    sellEntry: roundToTick(Math.pow(sqrtPP - 0.175, 2), tick),
    sellTP1:   roundToTick(Math.pow(sqrtPP - 0.405, 2), tick),
    sellTP2:   roundToTick(Math.pow(sqrtPP - 0.633, 2), tick),
    sellSL:    roundToTick(Math.pow(sqrtPP + 0.115, 2), tick),
  };
}

export const trendRoverIndicator: IndicatorDefinition = {
  id: "trend_rover",
  name: "Trend Rover",
  shortName: "TRV",
  category: "strategies",
  pane: "overlay",
  params: [
    { key: "alertEnabled", label: "Alert Notification", type: "boolean", default: true },
    { key: "formula", label: "Formula", type: "select", default: "sentinel", options: [
      { label: "Sentinel", value: "sentinel" },
      { label: "Crafter", value: "crafter" },
      { label: "Resistor", value: "resistor" },
    ]},
    { key: "timeframeSub", label: "Sub-Timeframe", type: "select", default: "m15", hidden: true, options: [
      { label: "Strategy-1 (M15)", value: "m15" },
      { label: "Strategy-2 (M30)", value: "m30" },
    ]},
    { key: "displayLevels", label: "Display Lines", type: "select", default: "yes", options: [{ label: "Yes", value: "yes" }, { label: "No", value: "no" }] },
    { key: "lineWidth", label: "Line Width", type: "number", default: 1, min: 1, max: 4, step: 1 },
    { key: "showBackground", label: "Candle Color", type: "select", default: "yes", options: [{ label: "Yes", value: "yes" }, { label: "No", value: "no" }] },
  ],
  plots: [
    { key: "buyEntry",  label: "Buy Entry",  color: "#2E7D32", style: "line", lineWidth: 2, lineStyle: 2, lastValueVisible: false },
    { key: "buyTP1",    label: "Buy TGT-1",  color: "#43A047", style: "line", lineWidth: 1, lineStyle: 2, lastValueVisible: false },
    { key: "buyTP2",    label: "Buy TGT-2",  color: "#66BB6A", style: "line", lineWidth: 1, lineStyle: 2, lastValueVisible: false },
    { key: "buySL",     label: "Buy SL",     color: "#A5D6A7", style: "line", lineWidth: 1, lineStyle: 3, lastValueVisible: false },
    { key: "sellEntry", label: "Sell Entry", color: "#C62828", style: "line", lineWidth: 2, lineStyle: 2, lastValueVisible: false },
    { key: "sellTP1",   label: "Sell TGT-1", color: "#E53935", style: "line", lineWidth: 1, lineStyle: 2, lastValueVisible: false },
    { key: "sellTP2",   label: "Sell TGT-2", color: "#EF5350", style: "line", lineWidth: 1, lineStyle: 2, lastValueVisible: false },
    { key: "sellSL",    label: "Sell SL",    color: "#EF9A9A", style: "line", lineWidth: 1, lineStyle: 3, lastValueVisible: false },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const formula = params.formula ?? "sentinel";
    const timeframeSub = params.timeframeSub ?? "m15";
    const displayLevels = params.displayLevels ?? "yes";
    const showBackground = params.showBackground ?? "yes";
    const len = candles.length;

    // ─── Label maps (needed both for empty-waiting and full dashboard) ─
    const formulaLabels: Record<string, string> = {
      sentinel: "Sentinel", crafter: "Crafter", resistor: "Resistor",
    };
    const tfLabels: Record<string, string> = {
      m15: "Strategy-1 (M15)", m30: "Strategy-2 (M30)",
    };

    // Helper: build a bare-minimum "waiting" dashboard (all levels null).
    // Used when the first candle of the day has NOT yet closed.
    function buildWaitingDashboard(): TrendRoverDashboard {
      return {
        formula: formulaLabels[formula] || "Sentinel",
        timeframeSub: tfLabels[timeframeSub] || "Strategy-1 (M15)",
        pivotPrice: null, dayRange: null,
        buyEntry: null, buyTP1: null, buyTP2: null, buySL: null,
        sellEntry: null, sellTP1: null, sellTP2: null, sellSL: null,
        buyActive: false, sellActive: false,
        buyTP1Hit: false, buyTP2Hit: false, buySLHit: false,
        sellTP1Hit: false, sellTP2Hit: false, sellSLHit: false,
        currentPrice: candles[len - 1]?.close ?? 0,
        buyLivePL: 0, sellLivePL: 0,
        buyBookedPL: 0, sellBookedPL: 0,
        buyDayPL: 0, sellDayPL: 0,
        buyTradeCount: 0, sellTradeCount: 0,
        buySLCount: 0, sellSLCount: 0,
        buyTP1Count: 0, buyTP2Count: 0,
        sellTP1Count: 0, sellTP2Count: 0,
      };
    }

    // Bare plot-data array with no lines/colors — metadata always included
    // so the metadataStore never retains stale data from a previous session.
    const emptyResult: PlotData[] = [
      { key: "buyEntry",  data: [], metadata: { dashboard: buildWaitingDashboard(), arrowMarkers: [], candleColors: [] } },
      { key: "buyTP1",    data: [] }, { key: "buyTP2", data: [] }, { key: "buySL", data: [] },
      { key: "sellEntry", data: [] }, { key: "sellTP1", data: [] },
      { key: "sellTP2",   data: [] }, { key: "sellSL", data: [] },
    ];

    if (len < 3) return emptyResult;

    const tick = inferTickSize(candles);
    const lw = Math.max(1, Math.min(4, Math.round(params.lineWidth ?? 1)));

    // ════════════════════════════════════════════════════════════
    // 1. Detect day boundaries and find first candle of each day
    // ════════════════════════════════════════════════════════════
    // Convert candle times to IST date for day boundary detection
    function getISTDate(unixSec: number): number {
      const d = new Date((unixSec + IST_OFFSET_SEC) * 1000);
      return d.getUTCFullYear() * 10000 + (d.getUTCMonth() + 1) * 100 + d.getUTCDate();
    }

    // Find day boundaries — first candle index per day
    const dayStarts: number[] = [];
    let prevDay = -1;
    for (let i = 0; i < len; i++) {
      const day = getISTDate(candles[i].time);
      if (day !== prevDay) {
        dayStarts.push(i);
        prevDay = day;
      }
    }

    if (dayStarts.length === 0) return emptyResult;

    // ════════════════════════════════════════════════════════════
    // 2. Compute levels for the LAST day using its first candle
    // ════════════════════════════════════════════════════════════
    // Use the last day's first candle for current levels
    const lastDayStart = dayStarts[dayStarts.length - 1];

    // Wait for the first candle to close before generating any signals/levels.
    // If the first candle of today is still the live (last) candle, its OHLC
    // values change every tick — suppress everything until it closes.
    // Return emptyResult (with a "waiting" dashboard in metadata) so the
    // metadataStore is cleared of any stale data from a previous session.
    if (lastDayStart === len - 1) {
      return emptyResult;
    }
    const fc = candles[lastDayStart]; // first candle of current day
    const pp = (fc.high + fc.low + fc.close) / 3.0;
    const dr = fc.high - fc.low;

    // StrategyValue: 0 for M15, 0.03 for M30
    const sv = timeframeSub === "m30" ? 0.03 : 0;

    let levels: Levels;
    if (formula === "crafter") {
      levels = computeCrafter(pp, dr, tick);
    } else if (formula === "resistor") {
      levels = computeResistor(pp, tick);
    } else {
      levels = computeSentinel(pp, dr, sv, tick);
    }

    // ════════════════════════════════════════════════════════════
    // 3. Buy/Sell activation & hit detection — supports re-entries
    //    Trade closes on TP2 or SL hit; returning to entry level re-activates
    // ════════════════════════════════════════════════════════════
    let buyActive = false, sellActive = false;
    // Per-trade hit flags — reset at each new entry
    let buyTP1Hit = false, buyTP2Hit = false, buySLHit = false;
    let sellTP1Hit = false, sellTP2Hit = false, sellSLHit = false;
    // Current trade's incrementally booked P&L (TP1 partial exit within active trade only)
    let buyCurrentBookedPL = 0, sellCurrentBookedPL = 0;
    // Cumulative day P&L — committed on every completed trade, NOT reset on re-entry
    let buyDayPL = 0, sellDayPL = 0;
    // Multi-trade counters
    let buyTradeCount = 0, sellTradeCount = 0;
    let buySLCount = 0, sellSLCount = 0;
    let buyTP1Count = 0, buyTP2Count = 0, sellTP1Count = 0, sellTP2Count = 0;
    // Prevent immediate re-entry on the SAME bar a trade closed
    let buyClosedBar = -1, sellClosedBar = -1;
    // Track latest activation time for "both active" colour tie-break
    let lastBuyActivationTime = 0, lastSellActivationTime = 0;
    // Re-entry gate:
    //   true  → activation allowed (initial first entry, or after SL hit)
    //   false → activation blocked (during active trade, or after TP2 — trade complete)
    let buyCanActivate = true, sellCanActivate = true;
    // Color to show on idle bars after a trade closes via TP2
    let postTradeColor: string = "";
    // Candle-event tracking
    const slHitBars = new Set<number>();
    const tp1HitBars = new Set<number>();
    const tp2BarColor = new Map<number, string>(); // bar index → TP2-win color
    const reEntryBuyBars = new Set<number>();
    const reEntrySellBars = new Set<number>();

    // Arrow markers collected inline
    const arrowMarkers: { time: number; direction: "long" | "short"; price: number; type: string; position: string }[] = [];

    // Per-bar colour state for current day (default blue; overwritten in scan loop)
    const barColorState: string[] = new Array(len).fill("#2196F3");

    // Start scanning from 3rd candle of the day (skip first 2)
    const scanStart = Math.min(lastDayStart + 2, len - 1);

    for (let i = scanStart; i < len; i++) {
      const h = candles[i].high;
      const l = candles[i].low;

      // ── Buy hit detection ──────────────────────────────────────
      if (buyActive) {
        if (!buyTP1Hit && h >= levels.buyTP1) {
          buyTP1Hit = true;
          buyCurrentBookedPL = levels.buyTP1 - levels.buyEntry; // partial exit at TP1
          buyTP1Count++;
          tp1HitBars.add(i);
          arrowMarkers.push({ time: candles[i].time, direction: "long", price: candles[i].high, type: "tp1", position: "above" });
        }
        if (!buyTP2Hit && h >= levels.buyTP2) {
          buyTP2Hit = true;
          buyCurrentBookedPL += levels.buyTP2 - levels.buyTP1; // incremental TP1→TP2
          buyDayPL += buyCurrentBookedPL;                       // commit full trade to day total
          buyCurrentBookedPL = 0;
          buyTP2Count++;
          tp2BarColor.set(i, "#00E676");                        // bright lime — buy TP2 win
          arrowMarkers.push({ time: candles[i].time, direction: "long", price: candles[i].high, type: "tp2", position: "above" });
          buyActive = false;
          buyClosedBar = i;
          buyCanActivate = false;                               // TP2 = trade complete, no re-entry
          postTradeColor = "#00C853";
        } else if (!buyTP2Hit && l <= levels.buySL) {
          buySLHit = true;
          if (!buyTP1Hit) {
            buyCurrentBookedPL = levels.buySL - levels.buyEntry; // full SL loss
          }
          // TP1 already hit → SL moved to break-even; commit TP1 profit already in buyCurrentBookedPL
          buyDayPL += buyCurrentBookedPL;
          buyCurrentBookedPL = 0;
          buySLCount++;
          arrowMarkers.push({ time: candles[i].time, direction: "long", price: candles[i].low, type: "sl", position: "below" });
          buyActive = false;
          buyClosedBar = i;
          buyCanActivate = true;                                // SL hit = allow re-entry
          postTradeColor = "";                                  // reset to neutral blue after SL
          slHitBars.add(i);
        }
      }

      // ── Sell hit detection ─────────────────────────────────────
      if (sellActive) {
        if (!sellTP1Hit && l <= levels.sellTP1) {
          sellTP1Hit = true;
          sellCurrentBookedPL = levels.sellEntry - levels.sellTP1; // partial exit at TP1
          sellTP1Count++;
          tp1HitBars.add(i);
          arrowMarkers.push({ time: candles[i].time, direction: "short", price: candles[i].low, type: "tp1", position: "below" });
        }
        if (!sellTP2Hit && l <= levels.sellTP2) {
          sellTP2Hit = true;
          sellCurrentBookedPL += levels.sellTP1 - levels.sellTP2; // incremental TP1→TP2
          sellDayPL += sellCurrentBookedPL;                        // commit full trade to day total
          sellCurrentBookedPL = 0;
          sellTP2Count++;
          tp2BarColor.set(i, "#FF1744");                           // bright red — sell TP2 win
          arrowMarkers.push({ time: candles[i].time, direction: "short", price: candles[i].low, type: "tp2", position: "below" });
          sellActive = false;
          sellClosedBar = i;
          sellCanActivate = false;                                  // TP2 = trade complete, no re-entry
          postTradeColor = "#FF1744";
        } else if (!sellTP2Hit && h >= levels.sellSL) {
          sellSLHit = true;
          if (!sellTP1Hit) {
            sellCurrentBookedPL = levels.sellEntry - levels.sellSL; // full SL loss (negative)
          }
          // TP1 already hit → SL moved to break-even; commit TP1 profit
          sellDayPL += sellCurrentBookedPL;
          sellCurrentBookedPL = 0;
          sellSLCount++;
          arrowMarkers.push({ time: candles[i].time, direction: "short", price: candles[i].high, type: "sl", position: "above" });
          sellActive = false;
          sellClosedBar = i;
          sellCanActivate = true;                                   // SL hit = allow re-entry
          postTradeColor = "";                                      // reset to neutral blue after SL
          slHitBars.add(i);
        }
      }

      // ── Activation — AFTER hit detection so entry bar can't trigger its own hits ──
      if (!buyActive && buyCanActivate && buyClosedBar !== i && h >= levels.buyEntry) {
        buyTP1Hit = false; buyTP2Hit = false; buySLHit = false;
        buyCurrentBookedPL = 0;                                    // fresh current-trade booked P&L
        buyActive = true;
        buyCanActivate = false;
        lastBuyActivationTime = candles[i].time;
        buyTradeCount++;
        if (buyTradeCount > 1) reEntryBuyBars.add(i);             // teal candle for re-entries
        arrowMarkers.push({ time: candles[i].time, direction: "long", price: candles[i].low, type: "entry", position: "below" });
      }
      if (!sellActive && sellCanActivate && sellClosedBar !== i && l <= levels.sellEntry) {
        sellTP1Hit = false; sellTP2Hit = false; sellSLHit = false;
        sellCurrentBookedPL = 0;                                   // fresh current-trade booked P&L
        sellActive = true;
        sellCanActivate = false;
        lastSellActivationTime = candles[i].time;
        sellTradeCount++;
        if (sellTradeCount > 1) reEntrySellBars.add(i);           // amber candle for re-entries
        arrowMarkers.push({ time: candles[i].time, direction: "short", price: candles[i].high, type: "entry", position: "above" });
      }

      // ── Per-bar candle colour ──────────────────────────────────
      // Priority: SL (orange) > TP2 win (bright) > TP1 milestone (gold)
      //           > re-entry (teal/amber) > active (green/red) > post-trade/neutral
      barColorState[i] = slHitBars.has(i)
        ? "#E67800"                  // orange  — SL hit candle
        : tp2BarColor.has(i)
        ? tp2BarColor.get(i)!        // lime green (buy) / bright red (sell) — TP2 win
        : tp1HitBars.has(i)
        ? "#FFD700"                  // gold    — TP1 milestone reached
        : reEntryBuyBars.has(i)
        ? "#00BCD4"                  // teal    — buy re-entry after SL
        : reEntrySellBars.has(i)
        ? "#FF9800"                  // amber   — sell re-entry after SL
        : (buyActive && sellActive)
        ? (lastSellActivationTime >= lastBuyActivationTime ? "#FF1744" : "#00C853")
        : buyActive  ? "#00C853"     // green   — buy trade active
        : sellActive ? "#FF1744"     // red     — sell trade active
        : postTradeColor || "#2196F3"; // post-TP2 signal color or neutral blue
    }

    // ════════════════════════════════════════════════════════════
    // 4. Dashboard metadata
    // ════════════════════════════════════════════════════════════
    const currentPrice = candles[len - 1]?.close ?? 0;

    const dashboard: TrendRoverDashboard = {
      formula: formulaLabels[formula] || "Sentinel",
      timeframeSub: tfLabels[timeframeSub] || "Strategy-1 (M15)",
      pivotPrice: roundToTick(pp, tick),
      dayRange: roundToTick(dr, tick),
      buyEntry: levels.buyEntry, buyTP1: levels.buyTP1,
      buyTP2: levels.buyTP2, buySL: levels.buySL,
      sellEntry: levels.sellEntry, sellTP1: levels.sellTP1,
      sellTP2: levels.sellTP2, sellSL: levels.sellSL,
      buyActive, sellActive,
      buyTP1Hit, buyTP2Hit, buySLHit,
      sellTP1Hit, sellTP2Hit, sellSLHit,
      currentPrice,
      buyLivePL: buyActive ? currentPrice - levels.buyEntry : 0,
      sellLivePL: sellActive ? levels.sellEntry - currentPrice : 0,
      buyBookedPL: buyCurrentBookedPL,   // current trade's TP1 partial (0 if no TP1 yet)
      sellBookedPL: sellCurrentBookedPL,
      buyDayPL, sellDayPL,
      buyTradeCount, sellTradeCount,
      buySLCount, sellSLCount,
      buyTP1Count, buyTP2Count, sellTP1Count, sellTP2Count,
    };

    // ════════════════════════════════════════════════════════════
    // 5. Build level lines (from current day start to end)
    // ════════════════════════════════════════════════════════════
    const bEntryData: { time: number; value: number }[] = [];
    const bT1Data: { time: number; value: number }[] = [];
    const bT2Data: { time: number; value: number }[] = [];
    const bSLData: { time: number; value: number }[] = [];
    const sEntryData: { time: number; value: number }[] = [];
    const sT1Data: { time: number; value: number }[] = [];
    const sT2Data: { time: number; value: number }[] = [];
    const sSLData: { time: number; value: number }[] = [];

    // ════════════════════════════════════════════════════════════
    // 6. Price lines — extend from day start to right edge + axis badges
    // ════════════════════════════════════════════════════════════
    const priceLines: IndicatorPriceLine[] = [];
    if (displayLevels !== "no") {
      // Lines start from the SECOND candle of the day — i.e. after the first candle has closed.
      // This ensures levels are never drawn while the first candle is still forming.
      const levelsFromTime = candles[lastDayStart + 1].time;
      // Show buy lines when buy is active OR neither is active (pre-activation preview)
      // Show sell lines when sell is active OR neither is active
      const pushBuy  = buyActive  || !sellActive;
      const pushSell = sellActive || !buyActive;
      if (pushBuy) {
        priceLines.push(
          { id: "tr-buy-entry", price: levels.buyEntry, color: "#2E7D32", title: "Buy Entry", lineVisible: true, lineWidth: lw, lineStyle: 2, axisLabelVisible: true, fromTime: levelsFromTime },
          { id: "tr-buy-tp1",   price: levels.buyTP1,   color: "#43A047", title: "Buy TGT-1", lineVisible: true, lineWidth: lw, lineStyle: 2, axisLabelVisible: true, fromTime: levelsFromTime },
          { id: "tr-buy-tp2",   price: levels.buyTP2,   color: "#66BB6A", title: "Buy TGT-2", lineVisible: true, lineWidth: lw, lineStyle: 2, axisLabelVisible: true, fromTime: levelsFromTime },
          { id: "tr-buy-sl",    price: levels.buySL,    color: "#A5D6A7", title: "Buy SL",    lineVisible: true, lineWidth: lw, lineStyle: 3, axisLabelVisible: true, fromTime: levelsFromTime },
        );
      }
      if (pushSell) {
        priceLines.push(
          { id: "tr-sell-entry", price: levels.sellEntry, color: "#C62828", title: "Sell Entry", lineVisible: true, lineWidth: lw, lineStyle: 2, axisLabelVisible: true, fromTime: levelsFromTime },
          { id: "tr-sell-tp1",   price: levels.sellTP1,   color: "#E53935", title: "Sell TGT-1", lineVisible: true, lineWidth: lw, lineStyle: 2, axisLabelVisible: true, fromTime: levelsFromTime },
          { id: "tr-sell-tp2",   price: levels.sellTP2,   color: "#EF5350", title: "Sell TGT-2", lineVisible: true, lineWidth: lw, lineStyle: 2, axisLabelVisible: true, fromTime: levelsFromTime },
          { id: "tr-sell-sl",    price: levels.sellSL,    color: "#EF9A9A", title: "Sell SL",    lineVisible: true, lineWidth: lw, lineStyle: 3, axisLabelVisible: true, fromTime: levelsFromTime },
        );
      }
    }

    // ════════════════════════════════════════════════════════════
    // 8. Candle colours — built from per-bar state in section 3
    // ════════════════════════════════════════════════════════════
    const candleColors: { time: number; color: string }[] = [];
    if (showBackground !== "no") for (let i = lastDayStart; i < len; i++) {
      candleColors.push({ time: candles[i].time, color: barColorState[i] });
    }

    return [
      { key: "buyEntry", data: bEntryData, priceLines, metadata: { dashboard, arrowMarkers, candleColors } },
      { key: "buyTP1", data: bT1Data },
      { key: "buyTP2", data: bT2Data },
      { key: "buySL", data: bSLData },
      { key: "sellEntry", data: sEntryData },
      { key: "sellTP1", data: sT1Data },
      { key: "sellTP2", data: sT2Data },
      { key: "sellSL", data: sSLData },
    ];
  },
};
